import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import LoginRoundedIcon from '@mui/icons-material/LoginRounded'
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded'
import KeyRoundedIcon from '@mui/icons-material/KeyRounded'
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import TvRoundedIcon from '@mui/icons-material/TvRounded'
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3'
import API from '../../services/API'
import { useNavigate } from 'react-router-dom'
import SplitPage from '../../components/SplitPage/SplitPage'
import EmailNotVerified from './EmailNotVerified'
import { TenantSettingsContext } from '../../App'
import { primeDemoSessionInfo } from '../../components/demo/DemoExperience'
import logo from '../../assets/images/ambunetLogo.png'
import CountryIndicator from './CountryIndicator'

// Demo tenants only: the "Try AmbuNet" entry. A short details form (name,
// work email, organisation; role, phone and the marketing opt-in are
// optional) — no account, no password — then straight into the demo. The
// server emails a link back in (?demoKey=…), which skips the form. Executes
// reCAPTCHA v3 (house pattern — see AccessForm.jsx) and POSTs /demo/start,
// whose success response has the same shape as /auth/login so the caller
// reuses applyLoginSuccess. Must render inside GoogleReCaptchaProvider.
const EMPTY_VISITOR = {
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  role: '',
  phone: '',
  marketingConsent: false,
}

const readDemoKey = () => {
  try {
    return new URLSearchParams(window.location.search).get('demoKey')
  } catch (e) {
    return null
  }
}

const clearDemoKey = () => {
  try {
    const url = new URL(window.location.href)
    url.searchParams.delete('demoKey')
    window.history.replaceState(null, '', url.pathname + url.search + url.hash)
  } catch (e) {
    // Leaving the key in the address bar is harmless.
  }
}

const DemoEntry = ({ onAuthenticated }) => {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [visitor, setVisitor] = useState(EMPTY_VISITOR)
  const [fieldErrors, setFieldErrors] = useState({})
  const [starting, setStarting] = useState(false)
  const [captchaFailed, setCaptchaFailed] = useState(false)
  const [startError, setStartError] = useState(false)
  const [linkExpired, setLinkExpired] = useState(false)
  const [demoKey] = useState(readDemoKey)
  const [usingLink, setUsingLink] = useState(!!demoKey)

  const update = (field) => (event) => {
    const value =
      field === 'marketingConsent' ? event.target.checked : event.target.value
    setVisitor((prev) => ({ ...prev, [field]: value }))
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const start = async (body) => {
    if (!executeRecaptcha) return
    setStarting(true)
    setCaptchaFailed(false)
    setStartError(false)
    try {
      const token = await executeRecaptcha('demo_start')
      const res = await API.post(
        '/demo/start',
        { recaptchaToken: token, ...body },
        { skipGlobalErrorHandler: true },
      )

      if (res.status === 200) {
        clearDemoKey()
        onAuthenticated(res.data)
        return
      }
      // The captcha-failure 403 body is a plain string, not JSON.
      if (res.status === 403 && res.data === 'failedRecaptcha') {
        setCaptchaFailed(true)
        return
      }
      if (res.status === 400 && res.data?.errorCode === 'demoDetailsRequired') {
        setFieldErrors(res.data.fields ?? {})
        return
      }
      if (res.status === 404 && res.data?.errorCode === 'demoLinkInvalid') {
        clearDemoKey()
        setUsingLink(false)
        setLinkExpired(true)
        return
      }
      setStartError(true)
    } catch (e) {
      setStartError(true)
    } finally {
      setStarting(false)
    }
  }

  // Coming back through the emailed link: straight in, once the captcha is
  // ready. A dead link falls back to the form.
  useEffect(() => {
    if (usingLink && demoKey && executeRecaptcha) start({ demoKey })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executeRecaptcha])

  const handleSubmit = (event) => {
    event.preventDefault()
    const errors = {}
    if (!visitor.firstName.trim()) errors.firstName = 'Enter your first name'
    if (!visitor.lastName.trim()) errors.lastName = 'Enter your last name'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(visitor.email.trim())) {
      errors.email = 'Enter a valid email address'
    }
    if (!visitor.company.trim()) errors.company = 'Enter your organisation'
    setFieldErrors(errors)
    if (Object.keys(errors).length) return
    start({ visitor })
  }

  if (usingLink) {
    return (
      <Stack spacing={1.5} alignItems="center" textAlign="center">
        <Typography variant="h5" fontWeight={900}>
          Welcome back
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {startError
            ? "We couldn't open the demo just now."
            : 'Opening your demo…'}
        </Typography>
        {startError && (
          <Button
            variant="contained"
            onClick={() => start({ demoKey })}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
          >
            Try again
          </Button>
        )}
      </Stack>
    )
  }

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      <Stack spacing={1.5}>
        <Box textAlign="center">
          <Typography variant="h5" fontWeight={900}>
            Try AmbuNet now
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Explore AmbuNet with realistic sample data. No account or password
            needed — just tell us who you are.
          </Typography>
        </Box>

        {linkExpired && (
          <Alert severity="info" onClose={() => setLinkExpired(false)}>
            That demo link has been replaced by a newer one. Fill in your
            details and we'll send you a fresh link.
          </Alert>
        )}
        {captchaFailed && (
          <Alert severity="warning" onClose={() => setCaptchaFailed(false)}>
            We couldn't verify you're human — please try again.
          </Alert>
        )}
        {startError && (
          <Alert severity="error" onClose={() => setStartError(false)}>
            We couldn't start the demo. Please try again in a moment.
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            label="First name"
            value={visitor.firstName}
            onChange={update('firstName')}
            error={!!fieldErrors.firstName}
            helperText={fieldErrors.firstName}
            autoComplete="given-name"
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Last name"
            value={visitor.lastName}
            onChange={update('lastName')}
            error={!!fieldErrors.lastName}
            helperText={fieldErrors.lastName}
            autoComplete="family-name"
            required
            fullWidth
            size="small"
          />
        </Stack>
        <TextField
          label="Work email"
          type="email"
          value={visitor.email}
          onChange={update('email')}
          error={!!fieldErrors.email}
          helperText={fieldErrors.email || "We'll email you a link back into the demo."}
          autoComplete="email"
          required
          fullWidth
          size="small"
        />
        <TextField
          label="Organisation"
          value={visitor.company}
          onChange={update('company')}
          error={!!fieldErrors.company}
          helperText={fieldErrors.company}
          autoComplete="organization"
          required
          fullWidth
          size="small"
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            label="Your role (optional)"
            value={visitor.role}
            onChange={update('role')}
            autoComplete="organization-title"
            fullWidth
            size="small"
          />
          <TextField
            label="Phone (optional)"
            type="tel"
            value={visitor.phone}
            onChange={update('phone')}
            autoComplete="tel"
            fullWidth
            size="small"
          />
        </Stack>
        <FormControlLabel
          control={
            <Checkbox
              checked={visitor.marketingConsent}
              onChange={update('marketingConsent')}
              size="small"
            />
          }
          label={
            <Typography variant="body2">
              Keep me updated about AmbuNet (optional)
            </Typography>
          }
        />
        <Typography variant="caption" color="text.secondary">
          We use these details to give you access to the demo, send you your
          demo link and, if you'd like, get in touch about AmbuNet for your
          organisation. See our{' '}
          <Link href="/privacy-policy" target="_blank" rel="noopener">
            privacy policy
          </Link>
          .
        </Typography>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          startIcon={<RocketLaunchRoundedIcon />}
          disabled={starting || !executeRecaptcha}
          sx={{
            borderRadius: 999,
            py: 1.4,
            textTransform: 'none',
            fontWeight: 800,
            fontSize: '1.05rem',
          }}
        >
          {starting ? 'Preparing your demo...' : 'View the demo'}
        </Button>
      </Stack>
    </Box>
  )
}

const LoginPage = ({ setUser, setOrgConfig }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [incorrectPassword, setIncorrectPassword] = useState(false)
  const [loginError, setLoginError] = useState(false)
  const [emailNotVerified, setEmailNotVerified] = useState(false)
  const [unableToLogin, setUnableToLogin] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Demo tenants collapse the credential form behind a subtle toggle — the
  // primary path is the anonymous "View the demo" CTA above it.
  const [credentialsOpen, setCredentialsOpen] = useState(false)

  const navigate = useNavigate()
  const { orgConfig, tenantInfo } = useContext(TenantSettingsContext)
  const orgSettings = orgConfig?.orgSettings ?? orgConfig ?? {}

  const orgLogo = orgSettings?.logo || logo
  const orgName = orgSettings?.orgName || 'AmbuNet'
  const isDemo = !!tenantInfo?.isDemo

  // Shared post-authentication steps — used by both a normal login and the
  // anonymous /demo/start entry, whose success response is identical to
  // /auth/login.
  const applyLoginSuccess = (data) => {
    // Demo tenants return the demo session state with the login
    // response — prime it so the welcome modal shows without waiting
    // for the /demo/session-info round trip.
    if (data.demo?.isDemoSession) {
      primeDemoSessionInfo(data.demo)
    }

    setUser(data.user)

    API.get('/admin/site-configuration')
      .then((res) => res.status === 200 && setOrgConfig(res.data))
      .catch((e) => console.log(e))
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    setIsSubmitting(true)
    setIncorrectPassword(false)
    setLoginError(false)
    setUnableToLogin(false)

    API.post(
      '/auth/login',
      {
        username: email,
        password,
      },
      { skipGlobalErrorHandler: true },
    )
      .then((res) => {
        if (res.status === 200) {
          applyLoginSuccess(res.data)
          return
        }

        if (res.status === 401) {
          setIncorrectPassword(true)
          return
        }

        if (res.data?.errorCode === 'emailNotVerified') {
          setEmailNotVerified(true)
          return
        }

        if (
          res.status === 403 &&
          res.data?.errorCode === 'TENANT_SUBSCRIPTION_EXPIRED'
        ) {
          setUnableToLogin(true)
          return
        }

        setLoginError(true)
      })
      .catch((e) => {
        if (e.response?.status === 401) {
          setIncorrectPassword(true)
        } else {
          setLoginError(true)
        }
      })
      .finally(() => setIsSubmitting(false))
  }

  return (
    <SplitPage>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            p: 3,
            background:
              'linear-gradient(135deg, #242F40 0%, #2c3e50 65%, #239AA4 150%)',
            color: 'white',
          }}
        >
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Box
              component="img"
              src={orgLogo}
              alt={`${orgName} logo`}
              sx={{
                maxWidth: 180,
                maxHeight: 90,
                objectFit: 'contain',
                bgcolor: 'white',
                borderRadius: 3,
                p: 1.2,
                boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
              }}
            />

            <Box>
              <Typography variant="overline" sx={{ opacity: 0.75 }}>
                Welcome to
              </Typography>
              <Typography variant="h5" fontWeight={900} color="white">
                {orgName}
              </Typography>
            </Box>

            <CountryIndicator />
          </Stack>
        </Box>

        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            {isDemo && (
              <>
                {/* The captcha provider is scoped to the demo entry only —
                    non-demo login rendering is untouched. */}
                <GoogleReCaptchaProvider
                  reCaptchaKey={process.env.RECAPTCHA_SITE_KEY}
                >
                  <DemoEntry onAuthenticated={applyLoginSuccess} />
                </GoogleReCaptchaProvider>

                <Divider />

                <Button
                  variant="text"
                  size="small"
                  onClick={() => setCredentialsOpen((prev) => !prev)}
                  endIcon={
                    <ExpandMoreRoundedIcon
                      sx={{
                        transform: credentialsOpen
                          ? 'rotate(180deg)'
                          : 'none',
                        transition: 'transform 0.2s',
                      }}
                    />
                  }
                  sx={{
                    alignSelf: 'center',
                    textTransform: 'none',
                    color: 'text.secondary',
                  }}
                >
                  Have demo credentials? Log in
                </Button>
              </>
            )}

            <Collapse in={!isDemo || credentialsOpen}>
              <Box component="form" noValidate onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h5" fontWeight={900}>
                      Sign in
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Access your organisation’s AmbuNet workspace.
                    </Typography>
                  </Box>

                  <TextField
                    required
                    fullWidth
                    id="email"
                    label="Email address"
                    name="email"
                    type="email"
                    onChange={(e) => setEmail(e.target.value)}
                    value={email}
                    disabled={emailNotVerified || isSubmitting}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    id="password"
                    onChange={(e) => setPassword(e.target.value)}
                    value={password}
                    disabled={emailNotVerified || isSubmitting}
                    InputLabelProps={{ shrink: true }}
                  />

                  {incorrectPassword && (
                    <Alert
                      severity="error"
                      onClose={() => setIncorrectPassword(false)}
                    >
                      Incorrect username or password.
                    </Alert>
                  )}

                  {loginError && (
                    <Alert severity="warning" onClose={() => setLoginError(false)}>
                      <AlertTitle>Unable to sign in</AlertTitle>
                      Please try again. If this continues, contact support.
                    </Alert>
                  )}

                  {emailNotVerified && (
                    <EmailNotVerified
                      email={email}
                      onClose={() => setEmailNotVerified(false)}
                    />
                  )}

                  {unableToLogin && (
                    <Alert severity="error">
                      We’re unable to log you in. Please contact your organisation’s
                      administrator.
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<LoginRoundedIcon />}
                    disabled={emailNotVerified || loginError || isSubmitting}
                    sx={{
                      borderRadius: 999,
                      py: 1.2,
                      textTransform: 'none',
                      fontWeight: 800,
                    }}
                  >
                    {isSubmitting ? 'Signing in...' : 'Sign in'}
                  </Button>

                  <Divider />

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                  >
                    {!isDemo && Number(tenantInfo?.subscriptionTier) > 1 && (
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<PersonAddAltRoundedIcon />}
                        onClick={() => navigate('/register')}
                        sx={{ borderRadius: 999, textTransform: 'none' }}
                      >
                        Register
                      </Button>
                    )}

                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<KeyRoundedIcon />}
                      onClick={() => navigate('/forgot-password')}
                      sx={{ borderRadius: 999, textTransform: 'none' }}
                    >
                      Forgot password?
                    </Button>
                  </Stack>

                  {!isDemo && Number(tenantInfo?.subscriptionTier) > 1 && (
                    <Button
                      size="small"
                      startIcon={<TvRoundedIcon />}
                      onClick={() => navigate('/wallboard')}
                      sx={{
                        textTransform: 'none',
                        color: 'text.secondary',
                        alignSelf: 'center',
                      }}
                    >
                      Set up a wallboard display
                    </Button>
                  )}
                </Stack>
              </Box>
            </Collapse>
          </Stack>
        </Box>
      </Box>
    </SplitPage>
  )
}

export default LoginPage
